import { skipToken, useQueries } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdDepartmentsTypeahead } from '@treadinc/horizon-api-spec';

import { DepartmentTypeahead } from '~hooks/useDepartment/models';
import { getDepartmentRateRequirements } from '~pages/Sales/sharedSales/rateRequirements';
import { fetchAllApiPages } from '~utils/fetchAllPages';

export const RATE_DEPARTMENT_MATCH_QUERY_KEY = 'rateDepartmentMatch';

const suggestedRateDepartments = [
  {
    name: 'FOB',
    searchQuery: 'fob',
    isMatch: (name: string) => getDepartmentRateRequirements(name).requiresMaterialRate,
  },
  {
    name: 'Delivery',
    searchQuery: 'deliver',
    isMatch: (name: string) => getDepartmentRateRequirements(name).requiresFreightRate,
  },
];

// A failed or pending check suggests nothing, so a name is only offered once the server says it is missing.
export default function useMissingRateDepartmentNamesQuery(
  companyId: string | undefined,
  isEnabled: boolean,
) {
  const results = useQueries({
    queries: suggestedRateDepartments.map(({ isMatch, searchQuery }) => ({
      queryKey: [RATE_DEPARTMENT_MATCH_QUERY_KEY, companyId, searchQuery],
      queryFn:
        companyId && isEnabled
          ? async ({ signal }) => {
              const departments = await fetchAllApiPages(
                (query, pageSignal) =>
                  getV1CompaniesCompanyIdDepartmentsTypeahead({
                    path: { 'company-id': companyId },
                    query: { ...query, 'search[query]': searchQuery },
                    signal: pageSignal,
                  }),
                DepartmentTypeahead.parse,
                { signal },
              );

              return departments.some((department) => isMatch(department.name));
            }
          : skipToken,
      retry: false,
    })),
  });

  const missingDepartmentNames =
    companyId && isEnabled
      ? suggestedRateDepartments
          .filter((_, index) => results[index]?.data === false)
          .map(({ name }) => name)
      : [];

  return { missingDepartmentNames };
}
